/* eslint-disable @typescript-eslint/no-explicit-any */
import { getI18n } from './i18n';
import type { Model } from '@kp/js-model';
import { ShWappenSvg, TgWappenSvg } from './images';

/**
 * Creates a function that can only be called "debounced", means, only all n milliseconds.
 *
 * @param      {Function}  fn      The function to execute
 * @param      {Number}    millis  The nr of milliseconds in which the function is NOT called again
 * @return     {Function}  a new Function that can rapidly be called, but is only invoked max all n milliseconds.
 */
export function debounce(fn: (...args: any) => void, millis = 350) {
    // hoisted timeout variable
    let timeout: number | null = null;
    const callbackFn = () => (timeout = null);

    return function (...args: any) {
        if (timeout) {
            return;
        }
        timeout = setTimeout(callbackFn, millis);
        fn(...args);
    };
}

export function intToFilesizeStr(bytes: number) {
    if (bytes / 1000000000 > 1) {
        return (bytes / 1000000000).toFixed(2) + ' GB';
    }
    if (bytes / 1000000 > 1) {
        return (bytes / 1000000).toFixed(2) + ' MB';
    }
    if (bytes / 1000 > 1) {
        return (bytes / 1000).toFixed(2) + ' KB';
    }
    return bytes + ' B';
}

export function todoAlert() {
    alert('TODO');
}

/**
 * Tries to make sense of the given "Error", which may be a string,
 * an error object, some kind of response.... you name it.
 * @param {any} error The error "thingie"
 * @return A string representing the error msg.
 */
export function errToStr(error: any): string {
    const vueI18n = getI18n();
    let msg = vueI18n.t('unknown_error');

    if (error) {
        // Try to extract a data property:
        const data = error?.response?.data || error?.response?.response?.data;

        if (data?.error?.msg) {
            msg = `${data.error.code}: ${data.error.msg}`;
        } else if (error?.response?.responseText) {
            const obj = JSON.parse(error.response.responseText);
            if (obj?.error?.msg) {
                msg = `${obj.error.code}: ${obj.error.msg}`;
            } else {
                msg = String(error.response.responseText);
            }
        } else if (error.responseText) {
            const obj = JSON.parse(error.responseText);
            if (obj?.error?.msg) {
                msg = `${obj.error.code}: ${obj.error.msg}`;
            } else {
                msg = String(error.responseText);
            }
        } else if (error?.error) {
            msg = `${error.error.code}: ${error.error.msg}`;
        } else if (typeof error === 'string') {
            try {
                const obj = JSON.parse(error);
                if (obj?.error?.msg) {
                    msg = `${obj.error.code}: ${obj.error.msg}`;
                } else {
                    msg = String(error);
                }
            } catch (e) {
                console.error(e, error);
                msg = String(error);
            }
        } else {
            msg = String(error);
        }
    }
    return msg;
}

export function reallyCancel(rec: Model | null): boolean {
    if (rec?.isDirty()) {
        if (!window.confirm(getI18n().t('really_cancel'))) {
            return false;
        }
    }
    return true;
}

export function fileExtToMime(ext: string) {
    switch (ext.toLocaleLowerCase()) {
        case 'pdf':
            return 'application/pdf';
        case 'doc':
        case 'docx':
            return 'application/msword';
        case 'ppt':
        case 'pptx':
            return 'application/vnd.ms-powerpoint';
        case 'xls':
            return 'application/vnd.ms-excel';
        case 'xlsx':
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        case 'jpg':
        case 'jpeg':
            return 'image/jpeg';
        case 'png':
            return 'image/png';
        default:
            return 'application/octet-stream';
    }
}

export function capitalize(s: string | null): string {
    s = s || '';
    return s[0].toUpperCase() + s.slice(1);
}

export function formatTelefonNumber(value: string | number) {
    const cleaned = ('' + value).replace(/\D/g, '');
    const match = cleaned.match(/^(0041|041|\+41|\+\+41|41)?(0|\(0\))?([1-9]\d{1})(\d{3})(\d{2})(\d{2})$/);
    if (match) {
        value = (match[1] ? '+41 ' : '0') + match[3] + ' ' + match[4] + ' ' + match[5] + ' ' + match[6];
    }
    return value;
}

export const addressTypes = {
    person: { lang_key: 'person', value: 'person' },
    organisation: { lang_key: 'organisation', value: 'organisation' }
};

export function completeStreetString(street: string | null) {
    if (street && street.toLowerCase().endsWith('str')) {
        return street + 'asse';
    }
    if (street && street.toLowerCase().endsWith('str.')) {
        return street.substring(0, street.length - 1) + 'asse';
    }
    return street;
}


/**
 * An array with all Kantone
 */
export const kantone = [
    { kuerzel: 'ag', kurz: 'AG', lang: 'Aargau' },
    { kuerzel: 'ai', kurz: 'AI', lang: 'Appenzell Innerrhoden' },
    { kuerzel: 'ar', kurz: 'AR', lang: 'Appenzell Ausserrhoden' },
    { kuerzel: 'be', kurz: 'BE', lang: 'Bern' },
    { kuerzel: 'bl', kurz: 'BL', lang: 'Basel-Landschaft' },
    { kuerzel: 'bs', kurz: 'BS', lang: 'Basel-Stadt' },
    { kuerzel: 'fr', kurz: 'FR', lang: 'Freiburg' },
    { kuerzel: 'ge', kurz: 'GE', lang: 'Genf' },
    { kuerzel: 'gl', kurz: 'GL', lang: 'Glarus' },
    { kuerzel: 'gr', kurz: 'GR', lang: 'Graubünden' },
    { kuerzel: 'ju', kurz: 'JU', lang: 'Jura' },
    { kuerzel: 'lu', kurz: 'LU', lang: 'Luzern' },
    { kuerzel: 'ne', kurz: 'NE', lang: 'Neuenburg' },
    { kuerzel: 'nw', kurz: 'NW', lang: 'Nidwalden' },
    { kuerzel: 'ow', kurz: 'OW', lang: 'Obwalden' },
    { kuerzel: 'sg', kurz: 'SG', lang: 'St. Gallen' },
    { kuerzel: 'sh', kurz: 'SH', lang: 'Schaffhausen', icon: ShWappenSvg },
    { kuerzel: 'so', kurz: 'SO', lang: 'Solothurn' },
    { kuerzel: 'sz', kurz: 'SZ', lang: 'Schwyz' },
    { kuerzel: 'tg', kurz: 'TG', lang: 'Thurgau', icon: TgWappenSvg },
    { kuerzel: 'ti', kurz: 'TI', lang: 'Tessin' },
    { kuerzel: 'ur', kurz: 'UR', lang: 'Uri' },
    { kuerzel: 'vd', kurz: 'VD', lang: 'Waadt' },
    { kuerzel: 'vs', kurz: 'VS', lang: 'Wallis' },
    { kuerzel: 'zg', kurz: 'ZG', lang: 'Zug' },
    { kuerzel: 'zh', kurz: 'ZH', lang: 'Zürich' }
];
